import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    Row,
    Col,
    Table,
    CardBody,
    Accordion,
    AccordionItem,
    AccordionHeader,
    AccordionBody,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Label,
    Input
} from 'reactstrap'
import { useSelector } from 'react-redux'
import { API_URL } from './utils/Constants/URLS'
import FocPackList from './FocPackList'

const FocViewDi = () => {
    const { sonumber } = useParams()
    const [diList, setDiList] = useState([])
    const [loading, setLoading] = useState(true)
    const [openDI, setOpenDI] = useState(null)

    // Modal for rejection
    const [declineModalOpen, setDeclineModalOpen] = useState(false)
    const [declineReason, setDeclineReason] = useState('')
    const [activeDiId, setActiveDiId] = useState(null)

    const [submitting, setSubmitting] = useState(false)
    const userDetails = useSelector(store => store.user)

    // For showing PackList creation
    const [showPackListDiId, setShowPackListDiId] = useState(null)

    const token = localStorage.getItem('token')

    const fetchDIs = async () => {
        try {
            setLoading(true)
            const res = await fetch(`${API_URL}foc/delivery-instruction`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const result = await res.json()
            // Filter by salesOrderNumber manually since we don't have a specific endpoint
            const allDIs = result?.data?.docs || []
            const filtered = allDIs.filter(d =>
                (d.salesOrderNumber === sonumber) ||
                (d.salesOrder?.salesOrderNumber === sonumber)
            )
            setDiList(filtered)
        } catch (error) {
            console.error('Error fetching FOC DIs:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (sonumber) fetchDIs()
    }, [sonumber, token])

    const formatDate = date =>
        date
            ? new Date(date).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            })
            : '-'

    const toggleDI = id => setOpenDI(prev => (prev === id ? null : id))

    const handleStatusChange = async (diId, status, reason = '') => {
        if (!diId) return
        setSubmitting(true)
        try {
            const body = { status }
            if (status === 'rejected') body.reasonForRejection = reason

            const res = await fetch(`${API_URL}foc/delivery-instruction/${diId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(body)
            })
            const result = await res.json()

            if (result.error) throw new Error(result.message || 'Failed to update status')

            // Refresh list
            fetchDIs()
            setDeclineModalOpen(false)
            setDeclineReason('')
            setActiveDiId(null)
            alert(`DI ${status} successfully`)
        } catch (e) {
            console.error('Error updating DI status:', e)
            alert('Error updating status')
        } finally {
            setSubmitting(false)
        }
    }

    const openDeclineModal = (diId) => {
        setActiveDiId(diId)
        setDeclineModalOpen(true)
    }

    const closeDeclineModal = () => {
        setDeclineModalOpen(false)
        setActiveDiId(null)
        setDeclineReason('')
    }


    if (loading) return <p className="text-center p-3">Loading Delivery Instructions...</p>

    if (diList.length === 0) return <p className="text-center p-3">No Delivery Instructions found for this Sales Order.</p>

    return (
        <CardBody className='p-4'>
            <h5 className='mb-4'>FOC Delivery Instructions</h5>
            <Accordion open={openDI} toggle={toggleDI}>
                {diList.map((di, idx) => {
                    const diId = di._id || `di-${idx}`
                    const status = di.status || 'pending'
                    const isApproved = status === 'approved'
                    const isRejected = status === 'rejected'

                    return (
                        <AccordionItem key={diId}>
                            <AccordionHeader targetId={diId}>
                                <div className='d-flex justify-content-between align-items-center w-100'>
                                    <span>
                                        <strong>{di.deliveryInstructionId || `DI #${idx + 1}`}</strong>
                                        <span className={`ms-2 badge ${isApproved ? 'bg-success' : isRejected ? 'bg-danger' : 'bg-warning'}`}>
                                            {status.toUpperCase()}
                                        </span>
                                    </span>
                                    <span className='me-3 text-muted'>{new Date(di.createdAt).toLocaleDateString()}</span>
                                </div>
                            </AccordionHeader>
                            <AccordionBody accordionId={diId}>
                                {showPackListDiId === diId ? (
                                    <FocPackList diId={diId} diData={di} onBack={() => setShowPackListDiId(null)} />
                                ) : (
                                    <>
                                        <Row className='mb-3'>
                                            {isRejected && (
                                                <Col md={12} className='mb-3'>
                                                    <div className='alert alert-danger'>
                                                        Reason: {di.reasonForRejection}
                                                    </div>
                                                </Col>
                                            )}

                                            <Col md={4}>
                                                <label className='form-label'>
                                                    <strong>Sales Order Number</strong>
                                                </label>
                                                <p>{di.salesOrderNumber || di.salesOrder?.salesOrderNumber || '-'}</p>
                                            </Col>

                                            <Col md={4}>
                                                <label className='form-label'>
                                                    <strong>DI Number</strong>
                                                </label>
                                                <p>{di.deliveryInstructionId || `DI #${idx + 1}`}</p>
                                            </Col>

                                            <Col md={4}>
                                                <label className='form-label'>
                                                    <strong>Customer PO Number</strong>
                                                </label>
                                                <p>{di.customerPONumber || di.salesOrder?.customerPONumber || '-'}</p>
                                            </Col>

                                            <Col md={4}>
                                                <label className='form-label'>
                                                    <strong>Date of DI</strong>
                                                </label>
                                                <p>{formatDate(di.createdAt)}</p>
                                            </Col>

                                            <Col md={4}>
                                                <label className='form-label'>
                                                    <strong>Store</strong>
                                                </label>
                                                <p>{di.store?.name ?? di.store ?? '-'}</p>
                                            </Col>
                                        </Row>

                                        <Table responsive striped>
                                            <thead>
                                                <tr>
                                                    <th>SI</th>
                                                    <th>Name</th>
                                                    <th>Part Code</th>
                                                    <th>UOM</th>
                                                    <th>QTY</th>
                                                    <th>Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {di.lineItems?.map((li, i) => (
                                                    <tr key={i}>
                                                        <td>{i + 1}</td>
                                                        <td>{li?.model?.modelName || '-'}</td>
                                                        <td>{li?.model?.partCode || '-'}</td>
                                                        <td>{li?.model?.uom || '-'}</td>
                                                        <td>{li?.quantity || 0}</td>
                                                        <td>{li?.value || 0}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>

                                        {/* Actions */}
                                        <div className='d-flex justify-content-end gap-2 mt-3'>
                                            {userDetails.role === 'commercial_manager' && status === 'pending' && (
                                                <>
                                                    <Button color='success' onClick={() => handleStatusChange(diId, 'approved')}>Approve</Button>
                                                    <Button color='danger' outline onClick={() => openDeclineModal(diId)}>Decline</Button>
                                                </>
                                            )}
                                            {userDetails.role === 'commercial_manager' && isApproved && (
                                                <Button color='primary' onClick={() => setShowPackListDiId(diId)}>Create Pack List</Button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </AccordionBody>
                        </AccordionItem>
                    )
                })}
            </Accordion>

            {/* Decline Modal */}
            <Modal isOpen={declineModalOpen} toggle={closeDeclineModal}>
                <ModalHeader toggle={closeDeclineModal}>Reject Delivery Instruction</ModalHeader>
                <ModalBody>
                    <Label>Reason for Rejection</Label>
                    <Input type='textarea' rows={4} value={declineReason} onChange={e => setDeclineReason(e.target.value)} />
                </ModalBody>
                <ModalFooter>
                    <Button color='secondary' onClick={closeDeclineModal}>Cancel</Button>
                    <Button color='danger' onClick={() => handleStatusChange(activeDiId, 'rejected', declineReason)} disabled={!declineReason.trim()}>Reject</Button>
                </ModalFooter>
            </Modal>
        </CardBody>
    )
}

export default FocViewDi
